import Analytics from '@/utils/analytics';
import ClearIcon from '@assets/icons/clear.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import {
  Insets,
  StyleProp,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useScannerKeyEvents } from '@/hooks/use-scanner-key-events';
import { useSocketMobileCapture } from '@/hooks/use-socket-mobile-capture';

const HIT_SLOP: Insets = {
  bottom: 16,
  left: 16,
  right: 16,
  top: 16,
};

export const SCAN_INPUT_DEBOUNCE_DELAY_MS = 5000;
export const SCAN_INPUT_HEIGHT = 48;

export interface Props {
  bottomComponent?: React.ReactNode;
  bottomContainerStyle?: StyleProp<ViewStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  errorTextOverride?: string | null;
  onClear: () => void;
  onSearch: (code: string) => Promise<void>;
  scanInputClearable?: boolean;
}

export const ScanInputField: React.FC<Props> = ({
  bottomComponent,
  bottomContainerStyle,
  containerStyle,
  errorTextOverride,
  onClear,
  onSearch,
  scanInputClearable = false,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const [styles, theme] = useStyles();
  const [scannedCode, setScannedCode] = useState('');
  const [searchText, setSearchText] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const resetInput = useCallback(() => {
    onClear();
    setErrorText(null);
    setSearchText('');
    inputRef.current?.clear();
  }, [onClear]);

  const submitScannedCode = useCallback(
    async (code: string) => {
      setScannedCode(code);
      resetInput();
      await onSearch(code);
    },
    [onSearch, resetInput],
  );

  useSocketMobileCapture(submitScannedCode);

  const { startListening, stopListening } =
    useScannerKeyEvents(submitScannedCode);

  useEffect(() => {
    if (errorTextOverride) {
      setErrorText(errorTextOverride);
    }
  }, [errorTextOverride]);

  const onSubmitSearch = useCallback(
    async (text: string) => {
      try {
        const trimmedInput = text.trim();
        if (trimmedInput) {
          await onSearch(trimmedInput);
        }
      } catch {
        setErrorText(i18n.t('toast.standard_error'));
      }
    },
    [onSearch],
  );

  const showClearButton = scanInputClearable
    ? Boolean(searchText || scannedCode)
    : Boolean(searchText);

  return (
    <View style={[styles.base, containerStyle]}>
      <View style={styles.textInputWrapper}>
        <View
          style={[
            styles.textInputContainer,
            isInputFocused ? styles.textInputFocused : null,
            errorText ? styles.textInputError : null,
          ]}
        >
          <SearchIcon fill={theme.colors.inactiveFill} height={16} />
          <TextInput
            accessibilityLabel="Scanner input"
            autoComplete="off"
            autoCorrect={false}
            onChangeText={(updatedText) => {
              stopListening();
              setErrorText(null);
              setSearchText(updatedText);
            }}
            onFocus={() => {
              Analytics.track('scan_input_focused', {
                restaurantUUID,
                supplierUUID,
              });
              stopListening();
              setScannedCode('');
              setIsInputFocused(true);

              // Use ref to select text instead of using selectTextOnFocus
              // to avoid conflicts with scanning listeners
              if (inputRef.current && searchText) {
                inputRef.current.setSelection(0, searchText.length);
              }
            }}
            onSubmitEditing={(e) => {
              onSubmitSearch(e.nativeEvent.text);
              startListening();
              setIsInputFocused(false);
            }}
            placeholder={scannedCode || i18n.t('scan.search_placeholder')}
            placeholderTextColor={theme.sk.color.text.hint}
            ref={inputRef}
            returnKeyType="search"
            style={styles.textInput}
            testID="scan-input-field"
          />
          {showClearButton ? (
            <TouchableOpacity
              focusable={false}
              hitSlop={HIT_SLOP}
              onPress={() => {
                resetInput();
                if (scannedCode) {
                  setScannedCode('');
                  setIsInputFocused(true);
                }
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }}
              style={styles.clearIcon}
              testID="clear-button"
            >
              <ClearIcon fill={theme.colors.primary} height={10} width={10} />
            </TouchableOpacity>
          ) : null}
        </View>
        <View style={[styles.bottomContainer, bottomContainerStyle]}>
          {bottomComponent ??
            (errorText ? (
              <Text style={[TYPOGRAPHY.xsmall, styles.errorText]}>
                {errorText}
              </Text>
            ) : null)}
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bottomContainer: {
    height: 20,
  },
  clearIcon: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: 24,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  errorText: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.error,
    marginTop: 4,
  },
  textInput: {
    ...FONTS.primary,
    color: theme.sk.color.text.primary,
    flex: 1,
    fontSize: theme.fontSizes.medium,
    height: SCAN_INPUT_HEIGHT,
  },
  textInputContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderColor: theme.sk.color.border.input.default,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 8,
    height: SCAN_INPUT_HEIGHT,
    justifyContent: 'space-between',
    paddingHorizontal: 12,
  },
  textInputError: {
    borderColor: theme.sk.color.text.error,
  },
  textInputFocused: {
    borderColor: theme.colors.accent,
  },
  textInputWrapper: {
    flex: 1,
    flexDirection: 'column',
  },
}));
